import { Editor } from "@tinymce/tinymce-react";
import { useEffect, useState } from "react";
import InputDropdown from "../../CommonComponents/InputDropdown";
import InputField from "../../CommonComponents/InputField";
import Radio from "../../CommonComponents/Radio";
import Textarea from "../../CommonComponents/Textarea";
import {
  GRADIOUS_ACCOUNT_ID,
  libTypeIds,
  MODE,
  TEST_SCOPE,
} from "../../../constants";
import ToggleButton from "../../CommonComponents/ToggleButton";
import { getDataFromStorage } from "../../../util";
import { useParams, useSearchParams } from "react-router-dom";
import DragAndDropUpload from "../../CommonComponents/DragAndDropUpload";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";
const testTypeOptions = [
  {
    value: libTypeIds.MCQ,
    label: "MCQ",
  },
  {
    value: libTypeIds.DESCRIPTIVE,
    label: "Descriptive",
  },
  {
    value: libTypeIds.CODING_SNIPPET,
    label: "Coding Snippet",
  },
  {
    value: libTypeIds.CODING_CHALLENGE,
    label: "Coding",
  },
  {
    value: libTypeIds.SQL,
    label: "SQL",
  },
];

const getImageName = (image) => {
  if (!image) return "";
  if (typeof File !== "undefined" && image instanceof File) return image.name;

  return String(image).split("/").pop();
};

const getImageSize = (image) => {
  if (typeof File === "undefined" || !(image instanceof File)) return "";

  const sizeInMb = image.size / (1024 * 1024);
  return `${sizeInMb < 0.1 ? sizeInMb.toFixed(2) : sizeInMb.toFixed(1)} MB`;
};

const UploadedImagePreview = ({ image, onOpen, onRemove, disabled }) => (
  <div
    className="uploadedFileContainer test-form-uploaded-image"
    onClick={onOpen}
    role="button"
    tabIndex={0}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    }}
  >
    <div className="uploadedFileSec">
      <div className="uploadedFileDetailSec">
        <div className="uploadedFileImage">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" stroke="#344054" strokeWidth="1.7" />
            <path d="M7 15L10 12L13 15L15 13L19 17" stroke="#344054" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="#0BA5EC" />
          </svg>
        </div>
        <div className="uploadedFileDetail">
          <div className="uploadedFileName">
            <span>{getImageName(image)}</span>
          </div>
          <div className="uploadedFileSize">
            {getImageSize(image) || "Click to view image"}
          </div>
        </div>
      </div>
      {!disabled && (
        <svg
          width="17"
          height="16"
          viewBox="0 0 17 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="uploadedFileRemoveIcon"
        >
          <path
            d="M2.5 3.99967H3.83333M3.83333 3.99967H14.5M3.83333 3.99967V13.333C3.83333 13.6866 3.97381 14.0258 4.22386 14.2758C4.47391 14.5259 4.81304 14.6663 5.16667 14.6663H11.8333C12.187 14.6663 12.5261 14.5259 12.7761 14.2758C13.0262 14.0258 13.1667 13.6866 13.1667 13.333V3.99967H3.83333ZM5.83333 3.99967V2.66634C5.83333 2.31272 5.97381 1.97358 6.22386 1.72353C6.47391 1.47348 6.81304 1.33301 7.16667 1.33301H9.83333C10.187 1.33301 10.5261 1.47348 10.7761 1.72353C11.0262 1.97358 11.1667 2.31272 11.1667 2.66634V3.99967M7.16667 7.33301V11.333M9.83333 7.33301V11.333"
            stroke="#F04438"
            strokeWidth="1.67"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
    <div className="uploadedFileStatusSec">
      <svg
        width="21"
        height="20"
        viewBox="0 0 21 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0.5" width="20" height="20" rx="10" fill="#039855" />
        <path d="M14.5 7L9 12.5L6.5 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="uploadedFileStatusText"> Uploaded Successfully</div>
    </div>
  </div>
);

const TestForm = ({
  type = libTypeIds.MCQ,
  testDetails,
  errors,
  onChange,
  mockuse,
  isPublished = false,
  tags = [],
}) => {
  const { getPreSignedUrl } = useS3FileManagement();
  const companyId = getDataFromStorage("companyId");
  const isGradiousUniversity = parseInt(companyId) === GRADIOUS_ACCOUNT_ID;
  const [searchParms] = useSearchParams();
  const {testid} = useParams();
  const isVideoInterview = searchParms.get("isVideoInterview") || null;
  const isVideoBool = isVideoInterview === "true";
  const isEdit = testid ? true : false;
  // console.log("isVideoBool:",isVideoBool);

  const [imageFile, setImageFile] = useState(testDetails?.image || null);
  const [imageError, setImageError] = useState("");

  const supportedFormat = ".jpeg, .jpg, .png";
  const supportedMimeTypes = [
    "image/png",
    "image/jpg",
    "image/apng",
    "image/jpeg",
  ];
  const MAX_IMAGE_SIZE = 2 * 1024 * 1024;

  const onChangeField = (field, value, extraUpdates = {}) => {
    onChange && onChange(field, value, extraUpdates);
  };

  useEffect(() => {
    setImageFile(testDetails?.image || null);
  }, [testDetails?.image]);

  const openImage = async () => {
    if (!imageFile) return;

    if (typeof File !== "undefined" && imageFile instanceof File) {
      const imageUrl = URL.createObjectURL(imageFile);
      window.open(imageUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(imageUrl), 60000);
      return;
    }

    if (/^https?:\/\//i.test(imageFile)) {
      window.open(imageFile, "_blank", "noopener,noreferrer");
      return;
    }

    try {
      const fileType = String(imageFile).split(".").pop();
      const preSignedData = await getPreSignedUrl(
        imageFile,
        undefined,
        fileType,
        "GET",
      );
      window.open(preSignedData.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Unable to open image", error);
      setImageError("Unable to open image. Please try again.");
    }
  };

  const handleImageUpload = (event) => {
    console.log("test details",testDetails);

  const files = Array.from(event.target.files || []);

  if (!files.length) return;

  if (files.length > 1) {
    setImageError("Multiple files are not allowed to upload");
    event.target.value = "";
    return;
  }

  const file = files[0];

  if (!supportedMimeTypes.includes(file.type)) {
    setImageError(
      `Unsupported file type: ${file.type}. Allowed formats are: ${supportedFormat}`
    );

    event.target.value = "";
    return;
  }

  if (file.size > MAX_IMAGE_SIZE) {
    setImageError("File size is too large.");
    event.target.value = "";
    return;
  }

  // IMPORTANT
  onChangeField("image", file);

  setImageFile(file);

  setImageError("");
  event.target.value = "";
};

  const handleImageDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files || []);
    if (!files.length) return;

    const fileEvent = {
      target: {
        files,
        value: "",
      },
    };
    handleImageUpload(fileEvent);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImageError("");
    onChangeField("image", null);
  };

  return (
    <div className="create-test-section create-test-form-section">
      <form className="create-test-form ">
        <div className="create-test-form-section-1">
          {mockuse === 1 && (
            <InputDropdown
              label="Test Type"
              name="libTypeId"
              placeholder="Select test type"
              options={testTypeOptions}
              value={parseInt(type)}
              onChange={(value) => onChangeField("libTypeId", value,
                {
                  scope: null,
                  videoAssessment: 0,
                  aiMode: 0,
                }
              )}
              hintText={errors?.libTypeId}
              destructive={!!errors?.libTypeId}
              disabled={isPublished || isEdit}
            />
          )}
          <InputField
            label="Test Name"
            name="name"
            value={testDetails.name}
            placeholder="Enter Test Name"
            onChange={(event) =>
              onChangeField(event.target.name, event.target.value)
            }
            hintText={errors?.name}
            destructive={!!errors?.name}
            disabled={isPublished}
          />
          <Textarea
            label="Description"
            name="description"
            value={testDetails.description || ""}
            placeholder="Enter description"
            onChange={(event) =>
              onChangeField(event.target.name, event.target.value)
            }
            hintText={errors.description}
            destructive={!!errors.description}
            disabled={isPublished}
          />
          {parseInt(type) !== libTypeIds.CODING_CHALLENGE ? (
            <div className="create-test-form-duration-section">
              <InputField
                label="Duration"
                value={testDetails.duration}
                min="0"
                placeholder="eg. 10"
                name="duration"
                type="number"
                className={`test-form-duration${
                  errors?.durationUnit
                    ? " test-form-duration-type-field-error"
                    : ""
                }`}
                onChange={(event) =>
                  onChangeField(event.target.name, event.target.value)
                }
                hintText={errors?.duration}
                destructive={!!errors?.duration}
                disabled={isPublished}
              />
              <InputDropdown
                placeholder="Min/Sec/Hrs"
                name="durationUnit"
                value={testDetails.durationUnit ?? "m"}
                className={`test-form-duration-type${
                  errors?.duration ? " test-form-duration-field-error" : ""
                }`}
                onChange={(value) => onChangeField("durationUnit", value)}
                options={[
                  {
                    label: "min/s",
                    value: "m",
                  },
                  {
                    label: "hour/s",
                    value: "h",
                  },
                  // {
                  //   label: "day/s",
                  //   value: "d",
                  // },
                ]}
                hintText={errors?.durationUnit}
                destructive={!!errors?.durationUnit}
                disabled={isPublished}
              />
            </div>
          ) : null}
          {mockuse === 1 ? (
            <>
              <div
                className={`create-test-form-scope-section${
                  errors?.scope ? " destructive" : ""
                }`}
              >
                <p className={`create-test-form-scope-title`}>Test For</p>
                <div className="create-test-form-scope-wrapper">
                  <div className="create-test-form-scope-options">
                    <Radio
                      text="Company Specific"
                      checked={testDetails.scope === TEST_SCOPE.COMPANY_SPECIFIC}
                      onChange={(checked) =>
                        onChangeField(
                          "scope",
                          checked
                            ? TEST_SCOPE.COMPANY_SPECIFIC
                            : null,
                        )
                      }
                      disabled={isPublished}
                    />
                    <Radio
                      text="Technology Specific"
                      checked={
                        testDetails.scope === TEST_SCOPE.TECHNOLOGY_SPECIFIC
                      }
                      onChange={(checked) =>
                        onChangeField(
                          "scope",
                          checked
                            ? TEST_SCOPE.TECHNOLOGY_SPECIFIC
                            : null,
                        )
                      }
                      disabled={isPublished}
                    />
                  </div>
                  
                  {isGradiousUniversity &&
                    parseInt(type) === libTypeIds.DESCRIPTIVE &&
                    // parseInt(type) !== libTypeIds.CODING_SNIPPET &&
                    mockuse === 1 &&
                    (!isVideoBool || !!testDetails.videoAssessment) ? (
                    <div className="create-test-form-video-assessment-section">
                      {!isVideoBool && (
                <>
                  {/* <div className="create-test-form-test-view-section create-test-form-test-util-section">
                    <ToggleButton
                      text={
                        <p>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clip-path="url(#clip0_2699_14105)">
                              <path
                                d="M18.3333 9.23806V10.0047C18.3323 11.8017 17.7504 13.5503 16.6744 14.9896C15.5984 16.4289 14.086 17.4818 12.3628 17.9913C10.6395 18.5008 8.79768 18.4396 7.11202 17.8169C5.42636 17.1941 3.98717 16.0431 3.00909 14.5356C2.03101 13.0281 1.56645 11.2448 1.68469 9.45166C1.80293 7.65853 2.49763 5.95167 3.66519 4.58562C4.83275 3.21958 6.41061 2.26755 8.16345 1.87152C9.91629 1.47549 11.7502 1.65668 13.3916 2.38806M18.3333 3.33329L9.99996 11.675L7.49996 9.17496"
                                stroke="#344054"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2699_14105">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>{" "}
                          Auto Evaluate:
                        </p>
                      }
                      disabled={isPublished}
                      pressed={!!testDetails.autoEvaluate}
                      onChange={(checked) =>
                        onChangeField("autoEvaluate", checked ? 1 : 0, checked)
                      }
                      reverse
                    />
                  </div> */}
                  <div className="create-test-form-test-view-section create-test-form-test-util-section">
                    <ToggleButton
                      text={
                        <p>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18.3333 7.44277C18.3333 6.93792 18.3333 6.6855 18.2335 6.56861C18.1468 6.46719 18.0169 6.41337 17.8839 6.42383C17.7307 6.43589 17.5522 6.61438 17.1952 6.97136L14.1666 9.99996L17.1952 13.0286C17.5522 13.3855 17.7307 13.564 17.8839 13.5761C18.0169 13.5866 18.1468 13.5327 18.2335 13.4313C18.3333 13.3144 18.3333 13.062 18.3333 12.5572V7.44277Z"
                              stroke="#344054"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M1.66663 8.16663C1.66663 6.7665 1.66663 6.06643 1.93911 5.53165C2.17879 5.06124 2.56124 4.67879 3.03165 4.43911C3.56643 4.16663 4.26649 4.16663 5.66663 4.16663H10.1666C11.5668 4.16663 12.2668 4.16663 12.8016 4.43911C13.272 4.67879 13.6545 5.06124 13.8941 5.53165C14.1666 6.06643 14.1666 6.76649 14.1666 8.16663V11.8333C14.1666 13.2334 14.1666 13.9335 13.8941 14.4683C13.6545 14.9387 13.272 15.3211 12.8016 15.5608C12.2668 15.8333 11.5668 15.8333 10.1666 15.8333H5.66663C4.26649 15.8333 3.56643 15.8333 3.03165 15.5608C2.56124 15.3211 2.17879 14.9387 1.93911 14.4683C1.66663 13.9335 1.66663 13.2334 1.66663 11.8333V8.16663Z"
                              stroke="#344054"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>{" "}
                          Video Assessment:
                        </p>
                      }
                      pressed={!!testDetails.videoAssessment}
                      onChange={(checked) =>
                        onChangeField("videoAssessment", checked ? 1 : 0)
                      }
                      disabled={isPublished}
                      reverse
                    />
                    <p className="create-test-form-test-util-radio-caption">
                      Allows learners to submit answers through video recording.
                    </p>
                  </div>
                </>
              )}
              {!testDetails.scope &&testDetails.videoAssessment && mockuse === 1 ? (
                <div className="create-test-form-test-view-section create-test-form-test-util-section">
                  <ToggleButton
                    text={<p>AI Mode:</p>}
                    pressed={!!testDetails.aiMode}
                    onChange={(checked) =>
                      onChangeField("aiMode", checked ? 1 : 0)
                    }
                    disabled={isPublished}
                    reverse
                  />
                </div>
              ) : null}
                    </div>
                    ) : null
                  }

                  {errors?.scope && (
                    <p className="create-test-form-scope-error">{errors.scope}</p>
                  )}

                </div>
                
              </div>
              
              {
                testDetails.scope && 
                (
                  <DragAndDropUpload
                    label="Logo"
                    file={imageFile}
                    supportFormat={supportedFormat}
                    disabled={isPublished}
                    onUpload={handleImageUpload}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleImageDrop}
                    onRemoveFile={handleRemoveImage}
                    uploadedFile={
                      <UploadedImagePreview
                        image={imageFile}
                        onOpen={openImage}
                        onRemove={handleRemoveImage}
                        disabled={isPublished}
                      />
                    }
                    hintText={errors?.image || imageError}
                    destructive={!!errors?.image || !!imageError}
                    className="centered-upload"
                  />
                )
              }

              <InputDropdown
                label="Tags"
                placeholder="Select the tags"
                options={tags}
                creatable
                multiple
                value={testDetails.tags ?? []}
                name="tags"
                onChange={(value) => onChangeField("tags", value)}
              />
            </>
          ) : null}
          {/* Instructions */}
          <div className="create-test-form-instruction-section">
            <p className="create-test-form-instruction-title">Instructions:</p>
            <Editor
              tinymceScriptSrc="/tinymce/tinymce.min.js"
              onEditorChange={(event) => onChangeField("instruction", event)}
              value={testDetails.instruction}
              disabled={isPublished}
              // onInit={(evt, editor) => (editorRef.current = editor)}
              init={{
                license_key: "gpl",
                readonly: true,
                menubar: false,
                branding: false,
                statusbar: false,
                elementpath: false,
                selector: "textarea",
                allow_html_in_named_anchor: true,
                plugins: [
                  "link",
                  "help",
                  "code",
                  "lists",
                  "image",
                  "table",
                  "media",
                  "anchor",
                  "editor",
                  "advlist",
                  "preview",
                  "charmap",
                  "wordcount",
                  "fullscreen",
                  "autoresize",
                  "visualblocks",
                  "searchreplace",
                  "insertdatetime",
                ],
                toolbar:
                  "undo redo | blocks | " +
                  "bold italic underline forecolor | link | alignleft aligncenter " +
                  "alignright alignjustify | bullist numlist outdent indent | " +
                  "removeformat | image | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | help |",

                content_style:
                  "body { font-family:Inter,Arial,sans-serif; font-size:14px }",
              }}
            />
          </div>
        </div>
        <div
          className={`create-test-form-section-2${
            mockuse === 1 &&
            (+type === libTypeIds.MCQ || +type === libTypeIds.CODING_SNIPPET || +type===libTypeIds.DESCRIPTIVE)
              ? " create-test-form-section-2-hidden"
              : ""
          }`}
        >
          {mockuse === 0 ? (
            <div className="create-test-form-test-mode-section create-test-form-test-util-section">
              <p className="create-test-form-test-mode-title create-test-form-test-util-title">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6667 10.4166V5.66663C16.6667 4.26649 16.6667 3.56643 16.3942 3.03165C16.1545 2.56124 15.7721 2.17879 15.3017 1.93911C14.7669 1.66663 14.0668 1.66663 12.6667 1.66663H7.33337C5.93324 1.66663 5.23318 1.66663 4.6984 1.93911C4.22799 2.17879 3.84554 2.56124 3.60586 3.03165C3.33337 3.56643 3.33337 4.26649 3.33337 5.66663V14.3333C3.33337 15.7334 3.33337 16.4335 3.60586 16.9683C3.84554 17.4387 4.22799 17.8211 4.6984 18.0608C5.23318 18.3333 5.93324 18.3333 7.33337 18.3333H10M11.6667 9.16663H6.66671M8.33337 12.5H6.66671M13.3334 5.83329H6.66671M12.0834 15.8333L13.75 17.5L17.5 13.75"
                    stroke="#344054"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>{" "}
                Test Mode:
              </p>
              <div className="create-test-form-test-mode-radio-group create-test-form-test-util-radio-group">
                <Radio
                  text="Practice Mode"
                  checked={testDetails.mode === MODE.PRACTICE_MODE}
                  onChange={(checked) =>
                    onChangeField(
                      "mode",
                      checked ? MODE.PRACTICE_MODE : MODE.TEST_MODE,
                    )
                  }
                  disabled={isPublished || isVideoBool}
                />
                <Radio
                  text="Test Mode"
                  checked={testDetails.mode === MODE.TEST_MODE}
                  onChange={(checked) =>
                    onChangeField(
                      "mode",
                      checked ? MODE.TEST_MODE : MODE.PRACTICE_MODE,
                    )
                  }
                  disabled={isPublished || isVideoBool}
                />
              </div>
              <p className="create-test-form-test-util-radio-caption">
                Practice Mode enables multiple attempts whereas Test enables
                single attempt.
              </p>
            </div>
          ) : null}
          {+type === libTypeIds.CODING_CHALLENGE && isGradiousUniversity && (
            <ToggleButton
              text="Enable Notebook :"
              name="enableNoteBook"
              pressed={!!testDetails.enableNoteBook}
              onChange={(value) => onChange("enableNoteBook", value)}
              reverse
              disabled={isPublished}
            />
          )}
          {parseInt(type) === libTypeIds.MCQ &&
          +testDetails.mode === MODE.TEST_MODE &&
          mockuse === 0 ? (
            <div className="create-test-form-test-view-section create-test-form-test-util-section">
              <p className="create-test-form-test-view-title create-test-form-test-util-title">
                Enable students to see questions in list view?
              </p>
              <div className="create-test-form-test-view-radio-group create-test-form-test-util-radio-group">
                <Radio
                  text="Yes"
                  checked={testDetails.listView === 1}
                  onChange={(checked) =>
                    onChangeField("listView", checked ? 1 : 0)
                  }
                />
                <Radio
                  text="No"
                  checked={testDetails.listView === 0}
                  onChange={(checked) =>
                    onChangeField("listView", checked ? 0 : 1)
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default TestForm;
